import { MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import { DraftConfiguration } from '@/types';
import { isRefDefined } from '@/utils/isRefDefined';
import { isSmartomat } from '@/utils/params';
import { pointerDownEvent, supportsPointerEvents } from '@/utils/pointerEvents';
import { useDraftDispatch } from '../DraftContext';
import { setSelectedConfigurationAction } from '../draftState/actions/setSelectedConfigurationAction';

// selector for elements that do not deselect the configuration
const IGNORED_ELEMENTS =
  '.settings *,.menu--mobile *,.menu__undo-redo *,.support *,#modal-container *,.configuration-handles,.mask-settings *,.configuration-error-bubble *';

// in some Desktop-Browsers the TouchEvent does not exist so we need another constructor that does not match any Event
const { TouchEvent = Image } = window;

export function useConfigurationSelector<SvgElementType extends SVGElement>(
  configuration: DraftConfiguration,
  isSelected: boolean
) {
  const target = useRef<SvgElementType | null>(null);
  const dispatch = useDraftDispatch();
  const deselect = useCallback(() => dispatch(setSelectedConfigurationAction()), [dispatch]);
  const deselectTimeout = useRef(-1);
  const preventDeselect = () => {
    clearTimeout(deselectTimeout.current);
    deselectTimeout.current = -1;
  };

  const selectConfiguration: MouseEventHandler = (event) => {
    if (!isSelected) {
      event.preventDefault();
      dispatch(setSelectedConfigurationAction(configuration));
    }
  };

  useEffect(() => {
    if (isSelected && !isSmartomat()) {
      const deselectConfiguration = (event: PointerEvent | MouseEvent) => {
        const isMultiTouch = supportsPointerEvents && !(event as PointerEvent).isPrimary;
        if (isMultiTouch && deselectTimeout.current > 0) {
          // remove the timer on the second touch because we assume the user tries to pinch the design
          preventDeselect();
        }

        const eventTargetElement = event.target as Element;
        if (
          !isRefDefined(target) || // configuration element has already been removed
          target.current.contains(eventTargetElement) || // event was triggered inside the configuration
          eventTargetElement.matches(IGNORED_ELEMENTS) || // event was triggered by one of the ignored elements
          isMultiTouch
        ) {
          return;
        }

        // add a timeout before deselecting the configuration
        //  if it's a touch event wait in case it's the start of a pinch
        //  otherwise just make sure another configuration can select first
        //   and the `deselect` gets ignored
        deselectTimeout.current = window.setTimeout(
          () => {
            deselect();
            deselectTimeout.current = -1;
          },
          event instanceof TouchEvent ? 150 : 25
        );
      };

      document.addEventListener(pointerDownEvent, deselectConfiguration);
      return () => {
        document.removeEventListener(pointerDownEvent, deselectConfiguration);
        preventDeselect();
      };
    }
  }, [isSelected, deselect]);

  return [target, selectConfiguration] as const;
}
